import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, X, Check, Filter, Building2, Users, Calendar } from 'lucide-react';
import { formatAcademicYearName } from '@/utils/academicYear';

interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface FilterDropdownProps {
  label: string;
  icon: React.ElementType;
  options: FilterOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  icon: Icon,
  options,
  selectedValues,
  onChange,
  placeholder = 'Tous',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const displayText = selectedValues.length === 0
    ? placeholder
    : selectedValues.length === 1
      ? options.find((o) => o.value === selectedValues[0])?.label || selectedValues[0]
      : `${selectedValues.length} sélectionnés`;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-all min-w-[140px] ${
          selectedValues.length > 0
            ? 'border-primary-300 bg-primary-50 text-primary-700'
            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
        }`}
      >
        <Icon size={16} className={selectedValues.length > 0 ? 'text-primary-600' : 'text-gray-400'} />
        <span className="truncate flex-1 text-left">{displayText}</span>
        {selectedValues.length > 0 ? (
          <span
            role="button"
            onClick={clearSelection}
            className="p-0.5 hover:bg-primary-200 rounded-full cursor-pointer"
          >
            <X size={14} />
          </span>
        ) : (
          <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-64 overflow-y-auto">
          <div className="px-3 py-2 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-500 uppercase">{label}</span>
          </div>
          {options.length === 0 ? (
            <div className="px-3 py-4 text-sm text-gray-500 text-center">
              Aucune option disponible
            </div>
          ) : (
            options.map((option) => (
              <button
                key={option.value}
                onClick={() => toggleOption(option.value)}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left"
              >
                <div
                  className={`w-4 h-4 rounded border flex items-center justify-center ${
                    selectedValues.includes(option.value)
                      ? 'bg-primary-600 border-primary-600'
                      : 'border-gray-300'
                  }`}
                >
                  {selectedValues.includes(option.value) && (
                    <Check size={12} className="text-white" />
                  )}
                </div>
                <span className="text-sm text-gray-700 truncate flex-1">{option.label}</span>
                {option.count !== undefined && (
                  <span className="text-xs text-gray-400">({option.count})</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export interface StaffFilters {
  role?: string[];
  academicYearId?: string[];
  campusId?: string[];
}

interface StaffFiltersPanelProps {
  filters: StaffFilters;
  onFiltersChange: (filters: StaffFilters) => void;
  staffData: Array<{
    role: string;
    campuses?: Array<{ id: string; name: string }>;
    academicYear?: { id: string; name: string } | null;
  }>;
  roleNames: Map<string, string>; // Map role code to French name
  availableAcademicYears?: Array<{ id: string; name: string }>; // Academic years from the database
  /** Headcount per academic year id, counting what the list actually shows. */
  academicYearCounts?: Record<string, number>;
}

export const StaffFiltersPanel: React.FC<StaffFiltersPanelProps> = ({
  filters,
  onFiltersChange,
  staffData,
  roleNames,
  availableAcademicYears = [],
  academicYearCounts,
}) => {
  // Get role options from staff data with counts
  const roleOptions: FilterOption[] = React.useMemo(() => {
    const roleMap = new Map<string, number>();

    staffData.forEach((s) => {
      if (s.role) {
        roleMap.set(s.role, (roleMap.get(s.role) || 0) + 1);
      }
    });

    // Convert to options array, using roleNames for labels
    return Array.from(roleMap.entries())
      .map(([role, count]) => ({
        value: role,
        label: roleNames.get(role) || role,
        count,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [staffData, roleNames]);

  // Get academic year options - use availableAcademicYears and count staff per year
  // Show all available academic years, with counts from staff data
  const academicYearOptions: FilterOption[] = React.useMemo(() => {
    // Count staff per academic year from staffData
    const staffCountByYear = new Map<string, number>();
    staffData.forEach((s) => {
      if (s.academicYear) {
        staffCountByYear.set(s.academicYear.id, (staffCountByYear.get(s.academicYear.id) || 0) + 1);
      }
    });

    // Show all available academic years (not just ones with staff)
    return availableAcademicYears
      .map((year) => ({
        value: year.id,
        label: formatAcademicYearName(year.name),
        // An active employee is listed whatever year is picked, so counting the
        // year stored on each account would contradict the list. The server
        // counts the same rule the filter applies.
        count: academicYearCounts?.[year.id] ?? staffCountByYear.get(year.id) ?? 0,
      }))
      .sort((a, b) => b.label.localeCompare(a.label)); // Most recent first
  }, [staffData, availableAcademicYears, academicYearCounts]);

  // Get campus options from staff data with counts
  const campusOptions: FilterOption[] = React.useMemo(() => {
    const campusMap = new Map<string, { name: string; count: number }>();

    staffData.forEach((s) => {
      if (s.campuses && s.campuses.length > 0) {
        s.campuses.forEach((campus) => {
          const existing = campusMap.get(campus.id);
          if (existing) {
            existing.count += 1;
          } else {
            campusMap.set(campus.id, { name: campus.name, count: 1 });
          }
        });
      }
    });

    return Array.from(campusMap.entries())
      .map(([id, { name, count }]) => ({
        value: id,
        label: name,
        count,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [staffData]);

  // Check if any filters are active
  const hasActiveFilters =
    (filters.role && filters.role.length > 0) ||
    (filters.academicYearId && filters.academicYearId.length > 0) ||
    (filters.campusId && filters.campusId.length > 0);

  const clearAllFilters = () => {
    onFiltersChange({
      role: undefined,
      academicYearId: undefined,
      campusId: undefined,
    });
  };

  // Show filters if there are any options
  const showRoleFilter = roleOptions.length > 0;
  const showAcademicYearFilter = academicYearOptions.length > 0; // Show if any academic years
  const showCampusFilter = campusOptions.length > 1; // Only show if 2+ campuses (no need when single campus)

  return (
    <div className="space-y-3">
      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-sm text-gray-500 mr-2">
          <Filter size={16} />
          <span>Filtres:</span>
        </div>

        {/* Role Filter */}
        {showRoleFilter && (
          <FilterDropdown
            label="Rôle"
            icon={Users}
            options={roleOptions}
            selectedValues={filters.role || []}
            onChange={(values) =>
              onFiltersChange({ ...filters, role: values.length > 0 ? values : undefined })
            }
            placeholder="Rôle"
          />
        )}

        {/* Année Académique Filter */}
        {showAcademicYearFilter && (
          <FilterDropdown
            label="Année académique"
            icon={Calendar}
            options={academicYearOptions}
            selectedValues={filters.academicYearId || []}
            onChange={(values) =>
              onFiltersChange({ ...filters, academicYearId: values.length > 0 ? values : undefined })
            }
            placeholder="Année"
          />
        )}

        {/* Campus Filter - only if 2+ campuses */}
        {showCampusFilter && (
          <FilterDropdown
            label="Campus"
            icon={Building2}
            options={campusOptions}
            selectedValues={filters.campusId || []}
            onChange={(values) =>
              onFiltersChange({ ...filters, campusId: values.length > 0 ? values : undefined })
            }
            placeholder="Campus"
          />
        )}

        {/* Clear All Button */}
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <X size={16} />
            <span>Effacer tout</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default StaffFiltersPanel;
